import FileDownload from "js-file-download";
import { DateTime } from "luxon";
import React, { useState } from "react";
import { modal2Actions } from "../../../../context/actions/modal2Actions";
import useStore from "../../../../context/useStore";
import downloadTools from "../../../../helpers/useDownload";
import ApercuPdf from "../../../common/ApercuPdf";
import InfoBulle from "../../../common/InfoBulle";
import SaveBtn from "../../../common/SaveBtn";

// En-tête posé par l'API sur /courriers/generer : il dit si le PDF servi est
// celui qui est parti à l'allocataire, une reconstitution, ou un simple aperçu.
const EN_TETE_ORIGINE = "x-document-origine";
const ORIGINE_RECONSTITUTION = "RECONSTITUTION";
const ORIGINE_APERCU = "APERCU";

// Ne concerne en pratique que les courriers envoyés avant que le BO ne
// conserve les documents : l'agent doit comprendre sans jargon qu'il ne voit
// pas l'original.
const TEXTE_RECONSTITUTION =
  "Ce courrier a été envoyé avant que le BO ne garde une copie des documents. Celui-ci a été refait après coup : il peut différer légèrement de celui reçu par l'allocataire.";

// Les EP de première génération n'ont jamais eu de copie conservée et leur
// regénération a été retirée : l'agent ne peut plus qu'en consulter l'historique.
const TEXTE_V1_NON_CONSULTABLE =
  "Courrier d'une EP de première génération : il ne peut plus être consulté ni regénéré. Seul l'historique d'envoi est conservé.";

const DownloadARDoc = ({ elem, docData, epBeginAt = null, isV2 = false }) => {
  const [state, dispatch] = useStore();
  // La liste des courriers est elle-même affichée dans la modale de l'EP :
  // l'aperçu s'ouvre dans la modale de second niveau, par-dessus.
  const m2Actions = modal2Actions(state, dispatch);
  const { SENDING_TYPES, SENDING_STATUS, EP_PARAMS } = state.constants.items;
  const [isDownloading, setIsDownloading] = useState(false);
  // L'origine arrive avec la liste des courriers (`documentOrigin`) : un
  // document déjà refait est signalé avant même qu'on l'ouvre. L'en-tête de la
  // réponse prend le relais au premier téléchargement, qui peut justement être
  // celui qui le refait.
  const [origineTelechargee, setOrigineTelechargee] = useState(null);
  const origineDocument = origineTelechargee || docData.documentOrigin || null;

  // Un courrier sans historique n'est jamais parti : ce qu'on affiche alors
  // est l'aperçu de ce qui partira, pas le document définitif.
  const estEnvoye = !!docData.historics?.length;
  const estApercu = isV2 && !estEnvoye;

  const parametres = { entityId: docData.entity, type: docData.type };

  const ouvrirApercu = ({ blob, headers, filename }) => {
    const origine = headers?.[EN_TETE_ORIGINE] || null;
    setOrigineTelechargee(origine);

    // Le titre suit ce que l'API a réellement servi, pas l'état de la liste :
    // si l'envoi s'est terminé entre l'affichage et le clic, c'est bien le
    // courrier parti qu'on montre.
    const titre = (origine ? origine === ORIGINE_APERCU : estApercu)
      ? "Aperçu du courrier"
      : "Courrier envoyé";

    m2Actions.updateModal({
      isOpen: true,
      content: (
        <>
          <h5 className="d-flex align-items-center mb-3">
            {titre}
            {origine === ORIGINE_RECONSTITUTION && (
              <span className="ml-2">
                <InfoBulle
                  texte={TEXTE_RECONSTITUTION}
                  icone="fa-exclamation-triangle"
                  className="text-warning"
                  place="bottom"
                />
              </span>
            )}
          </h5>
          <ApercuPdf blob={blob} titre={titre} />
        </>
      ),
      actions: (
        <button
          className="btn btn-primary"
          onClick={() => FileDownload(blob, filename || "courrier.pdf")}
        >
          <i className="fa fa-download mr-2" aria-hidden="true" />
          Télécharger
        </button>
      ),
    });
  };

  const downloadElem = async () => {
    setIsDownloading(true);
    try {
      ouvrirApercu(
        await downloadTools.fetchFile(`courriers/generer`, parametres),
      );
    } catch (err) {
      // L'erreur est déjà signalée à l'agent par useDownload.
    } finally {
      setIsDownloading(false);
    }
  };

  let sendArDate = epBeginAt
    ? DateTime.fromISO(epBeginAt)
        .minus({ days: EP_PARAMS.find((epp) => epp.id == "SEND_AR").value })
        .toFormat("dd/MM/yyyy")
    : null;

  const libelleBouton = estEnvoye ? "Voir le courrier envoyé" : "Voir l'aperçu";

  // La couleur dit ce que l'agent va obtenir : un aperçu (bleu) ou le courrier
  // envoyé (vert). Un document refait après coup reste vert : seule l'icône
  // d'avertissement le signale.
  const couleurBouton = estApercu ? "btn-info" : "btn-success";

  const historique = docData.historics.length ? (
    <ol className="mt-2 pl-0">
      {docData.historics?.map((histo, hk) => (
        <li key={`hist${elem.id}hist${hk}`}>
          {histo.data || (
            <>
              <small>
                <strong className="text-info">
                  {SENDING_STATUS.find((ss) => ss.id == histo.status).name}
                </strong>{" "}
                - {DateTime.fromISO(histo.updatedAt).toFormat("dd/MM/yyyy")}
              </small>
            </>
          )}
        </li>
      )) || ""}
    </ol>
  ) : sendArDate ? (
    <div>
      <small>
        <strong>Envoi prévu : </strong>
        {sendArDate}
      </small>
    </div>
  ) : null;

  // Les courriers des EP de première génération ne se regénèrent plus : le
  // métier a confirmé ne pas en avoir besoin. Seul l'historique d'envoi reste.
  if (!isV2) {
    return (
      <div className="d-flex flex-column justify-content-around align-items-start">
        <div className="d-flex align-items-center text-muted">
          {SENDING_TYPES.find((st) => st.id == docData.type)?.name || ""}
          <span className="ml-2">
            <InfoBulle texte={TEXTE_V1_NON_CONSULTABLE} />
          </span>
        </div>
        {historique}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column justify-content-around align-items-start">
      <div className="d-flex align-items-center">
        <SaveBtn
          data-priv="download_ar_mail"
          save={downloadElem}
          text={
            <>
              <i className="fa fa-eye mr-2" aria-hidden="true" />
              {libelleBouton}
            </>
          }
          isSaving={isDownloading}
          margin={"mt-0"}
          className="align-items-center"
          type={couleurBouton}
        />

        {estApercu && (
          <span className="ml-2">
            <InfoBulle texte="Aperçu : ce courrier n'a pas encore été envoyé à l'allocataire. Le document définitif sera disponible ici après son envoi." />
          </span>
        )}

        {origineDocument === ORIGINE_RECONSTITUTION && (
          <span className="ml-2">
            <InfoBulle
              texte={TEXTE_RECONSTITUTION}
              icone="fa-exclamation-triangle"
              className="text-warning"
            />
          </span>
        )}
      </div>

      {historique}
    </div>
  );
};

export default DownloadARDoc;
